import { Box, IconButton, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CustomTableSortLabel } from '@components';
import { Column } from '../../../../components/ImmutableDataTable/ImmutableDataTable';
import { USER_STATUS, USER_STATUS_MAP } from '@constants/constants';
import { formatDate } from '@utils/common';
import { UserManagementTableRow } from '../hooks/useUserManagementTable';
import TableFilterDropdown from '../../../../components/TableFilterDropdown';
import { PVX_ROLE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '../constants';

const cellTextSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '1.25rem',
  textAlign: 'left',
};

const columnStyles = {
  customHeaderStyles: { textAlign: 'left', whiteSpace: 'nowrap', fontSize: '0.875rem' },
  customCellStyles: { textAlign: 'left', padding: '0.25rem 0.5rem' },
};

const formatRoleLabel = (role: string) =>
  role.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());

const formatStatusLabel = (status: string) => USER_STATUS_MAP[status as USER_STATUS] ?? status;

type UserManagementTableColumnOptions = {
  onActionClick: (event: React.MouseEvent<HTMLElement>, row: UserManagementTableRow) => void;
  sort: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (sortKey: string) => void;
  roleFilter: string;
  onRoleFilterChange: (filter: string) => void;
  accessTemplateTypeFilter: string;
  onAccessTemplateTypeFilterChange: (filter: string) => void;
  accessTemplateTypeFilterOptions: { label: string; value: string }[];
  statusFilter: string;
  onStatusFilterChange: (filter: string) => void;
};

export const getUserManagementTableColumns = ({
  onActionClick,
  sort,
  sortOrder,
  onSort,
  roleFilter,
  onRoleFilterChange,
  accessTemplateTypeFilter,
  onAccessTemplateTypeFilterChange,
  accessTemplateTypeFilterOptions,
  statusFilter,
  onStatusFilterChange,
}: UserManagementTableColumnOptions): Column<UserManagementTableRow>[] => [
  {
    key: 'companyName',
    label: 'Company',
    ...columnStyles,
    render: (value: UserManagementTableRow['companyName']) => (
      <Typography sx={{ ...cellTextSx, minWidth: '200px', whiteSpace: 'nowrap' }}>
        {value || '-'}
      </Typography>
    ),
  },
  {
    key: 'user',
    label: 'User',
    ...columnStyles,
    render: (value: UserManagementTableRow['user']) => {
      const email = String(value || '-');
      return <Typography sx={cellTextSx}>{email}</Typography>;
    },
  },
  {
    key: 'role',
    label: 'Role',
    ...columnStyles,
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.25rem',
          whiteSpace: 'nowrap',
        }}
      >
        Role
        <TableFilterDropdown
          label="Role"
          options={PVX_ROLE_FILTER_OPTIONS}
          showSingleSelectIcon
          selected={roleFilter ? [roleFilter] : []}
          setSelected={(filters) => {
            onRoleFilterChange(filters.length ? filters[filters.length - 1] : '');
          }}
        />
      </Box>
    ),
    render: (value: UserManagementTableRow['role']) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {value ? formatRoleLabel(value) : '-'}
      </Typography>
    ),
  },
  {
    key: 'internalUserAccessTemplateType',
    label: 'Type',
    ...columnStyles,
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.25rem',
          whiteSpace: 'nowrap',
        }}
      >
        Type
        <TableFilterDropdown
          label="Type"
          options={accessTemplateTypeFilterOptions}
          showSingleSelectIcon
          selected={accessTemplateTypeFilter ? [accessTemplateTypeFilter] : []}
          setSelected={(filters) =>
            onAccessTemplateTypeFilterChange(filters.length ? filters[filters.length - 1] : '')
          }
        />
      </Box>
    ),
    render: (value: UserManagementTableRow['internalUserAccessTemplateType']) => {
      const accessTemplateType = accessTemplateTypeFilterOptions.find(
        (option) => option.value === value,
      );
      return (
        <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
          {accessTemplateType?.label || '-'}
        </Typography>
      );
    },
  },
  {
    key: 'joiningDate',
    label: 'Joining Date',
    ...columnStyles,
    renderHeader: () => (
      <Box sx={{ minWidth: '100%' }}>
        <CustomTableSortLabel
          label="Joining Date"
          active={sort === 'joiningDate'}
          sortDirection={sort === 'joiningDate' ? sortOrder : 'ASC'}
          onSort={() => onSort('joiningDate')}
          labelStyles={{ ...columnStyles.customHeaderStyles }}
        />
      </Box>
    ),
    render: (value: UserManagementTableRow['joiningDate']) => (
      <Typography sx={{ ...cellTextSx, minWidth: '120px' }}>
        {value ? formatDate(value) : '-'}
      </Typography>
    ),
  },
  {
    key: 'userStatus',
    label: 'Status',
    ...columnStyles,
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.25rem',
          whiteSpace: 'nowrap',
        }}
      >
        Status
        <TableFilterDropdown
          label="Status"
          options={STATUS_FILTER_OPTIONS}
          showSingleSelectIcon
          selected={statusFilter ? [statusFilter] : []}
          setSelected={(filters) =>
            onStatusFilterChange(filters.length ? filters[filters.length - 1] : '')
          }
        />
      </Box>
    ),
    render: (value: UserManagementTableRow['userStatus']) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {value ? formatStatusLabel(value) : '-'}
      </Typography>
    ),
  },
  {
    // Virtual column — `actions` is not on the row type; satisfies Column<T>'s `keyof T` constraint
    key: 'actions' as keyof UserManagementTableRow,
    label: 'Actions',
    ...columnStyles,
    render: (_value, row) => (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={(event) => {
            event.stopPropagation();
            onActionClick(event, row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
    ),
  },
];
